import React, { useState, useEffect, useRef } from "react";
import styles from "./month-year-picker.module.css";
import { DateRange } from "BEDROCK/shared-components/filter-chips";
import { CTAButton } from "BEDROCK/shared-components/cta-button";

interface MonthYearPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (range: DateRange) => void;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getMonthRange(month: number, year: number): DateRange {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0, 23, 59, 59);

  return {
    after: Math.floor(startDate.getTime() / 1000),
    before: Math.floor(endDate.getTime() / 1000),
  };
}

function getAvailableYears(): number[] {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let i = 0; i < 5; i++) {
    years.push(currentYear - i);
  }
  return years;
}

export function MonthYearPicker({ isOpen, onClose, onSelect }: MonthYearPickerProps) {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleApply = () => {
    const range = getMonthRange(selectedMonth, selectedYear);
    onSelect(range);
    onClose();
  };

  if (!isOpen) return null;

  const availableYears = getAvailableYears();

  return (
    <div className={styles.overlay}>
      <div className={styles.dropdown} ref={dropdownRef}>
        <div className={styles.header}>Select Month & Year</div>

        <div className={styles.selectors}>
          <div className={styles.selectorGroup}>
            <label className={styles.label}>Month</label>
            <select
              className={styles.select}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {MONTHS.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.selectorGroup}>
            <label className={styles.label}>Year</label>
            <select
              className={styles.select}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.actions}>
          <CTAButton variant="SECONDARY" onClick={onClose}>
            Cancel
          </CTAButton>
          <CTAButton variant="PRIMARY" onClick={handleApply}>
            Apply
          </CTAButton>
        </div>
      </div>
    </div>
  );
}
